import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, Plus, Trash2, X, Ban } from "lucide-react";
import { useClosurePeriods } from "@/hooks/useClosurePeriods";
import { useChateaux } from "@/hooks/useChateaux";
import { useSumos } from "@/hooks/useSumos";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export const ClosurePeriodsManager = () => {
  const { closures, loading, createClosure, deleteClosure, toggleClosure } = useClosurePeriods();
  const { chateaux } = useChateaux();
  const { sumos } = useSumos();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [raison, setRaison] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const allProducts = [
    ...chateaux.map(c => ({ id: c.id, name: c.nom, type: "chateau" as const })),
    ...sumos.map(s => ({ id: s.id, name: s.title, type: "sumo" as const })),
  ];

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    setSelectedProducts(checked ? allProducts.map(p => p.id) : []);
  };

  const handleToggleProduct = (productId: string) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
    setSelectAll(false);
  };

  const handleSubmit = async () => {
    if (!dateDebut || !dateFin || selectedProducts.length === 0) return;
    await createClosure({
      date_debut: dateDebut,
      date_fin: dateFin,
      raison: raison || undefined,
      affected_product_ids: selectedProducts,
    });
    setDialogOpen(false);
    setDateDebut("");
    setDateFin("");
    setRaison("");
    setSelectedProducts([]);
    setSelectAll(false);
  };

  const getProductName = (id: string) => {
    const chateau = chateaux.find(c => c.id === id);
    if (chateau) return chateau.nom;
    const sumo = sumos.find(s => s.id === id);
    if (sumo) return sumo.title;
    return id;
  };

  const formatDate = (d: string) => {
    try {
      return format(new Date(d), "dd MMM yyyy", { locale: fr });
    } catch {
      return d;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-display font-semibold flex items-center gap-2">
          <Ban className="w-5 h-5" />
          Périodes de Fermeture
        </h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une fermeture
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nouvelle période de fermeture</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date début</Label>
                  <Input
                    type="date"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Date fin</Label>
                  <Input
                    type="date"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Raison (optionnel)</Label>
                <Input
                  value={raison}
                  onChange={(e) => setRaison(e.target.value)}
                  placeholder="Ex: Vacances, maintenance..."
                />
              </div>
              <div>
                <Label className="mb-3 block">Produits indisponibles pendant cette période</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-60 overflow-y-auto">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Checkbox
                      checked={selectAll}
                      onCheckedChange={handleSelectAll}
                      id="select-all"
                    />
                    <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                      Tout sélectionner
                    </label>
                  </div>

                  {chateaux.length > 0 && (
                    <>
                      <p className="text-xs font-semibold text-muted-foreground mt-2">🏰 Châteaux</p>
                      {chateaux.map(c => (
                        <div key={c.id} className="flex items-center gap-2">
                          <Checkbox
                            checked={selectedProducts.includes(c.id)}
                            onCheckedChange={() => handleToggleProduct(c.id)}
                            id={`product-${c.id}`}
                          />
                          <label htmlFor={`product-${c.id}`} className="text-sm cursor-pointer">
                            {c.nom}
                          </label>
                        </div>
                      ))}
                    </>
                  )}

                  {sumos.length > 0 && (
                    <>
                      <p className="text-xs font-semibold text-muted-foreground mt-2">🥋 Sumos</p>
                      {sumos.map(s => (
                        <div key={s.id} className="flex items-center gap-2">
                          <Checkbox
                            checked={selectedProducts.includes(s.id)}
                            onCheckedChange={() => handleToggleProduct(s.id)}
                            id={`product-${s.id}`}
                          />
                          <label htmlFor={`product-${s.id}`} className="text-sm cursor-pointer">
                            {s.title}
                          </label>
                        </div>
                      ))}
                    </>
                  )}
                </div>
                {selectedProducts.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedProducts.length} produit(s) sélectionné(s)
                  </p>
                )}
              </div>
              <Button
                onClick={handleSubmit}
                disabled={!dateDebut || !dateFin || selectedProducts.length === 0}
                className="w-full"
              >
                Créer la période de fermeture
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Chargement...</p>
      ) : closures.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Aucune période de fermeture configurée.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {closures.map(closure => (
            <Card key={closure.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-primary" />
                      <span className="font-medium">
                        {formatDate(closure.date_debut)} → {formatDate(closure.date_fin)}
                      </span>
                      <Badge variant={closure.is_active ? "destructive" : "secondary"}>
                        {closure.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    {closure.raison && (
                      <p className="text-sm text-muted-foreground mb-2">{closure.raison}</p>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {closure.affected_product_ids.map(pid => (
                        <Badge key={pid} variant="outline" className="text-xs">
                          {getProductName(pid)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleClosure(closure.id, closure.is_active)}
                    >
                      {closure.is_active ? "Désactiver" : "Activer"}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteClosure(closure.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
